import { useState, useMemo } from "react";
import { Stack, Title, Divider, Loader, Card, Text } from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  useGetRegistrationCustomFields,
  useOrderRegistrationCustomFields,
} from "../../api/registrationCustomFields";
import CustomFieldCard from "./CustomFieldCard";

function CustomFieldsList({ event, appliedTo, registrationsEnabled = true }) {
  const { data, isLoading } = useGetRegistrationCustomFields(event?.id);
  const orderMutation = useOrderRegistrationCustomFields();
  const [localOrder, setLocalOrder] = useState(null);

  const getSortedFields = (fields, type) =>
    fields
      .filter((f) => f.appliedTo === type)
      .sort((a, b) => a.order - b.order);

  const { registrationsFields, ticketsFields } = useMemo(() => {
    const fields = data?.customFields || [];
    const allRegistrations = getSortedFields(fields, "registrations");
    const allTickets = getSortedFields(fields, "ticket");

    if (localOrder) {
      const apiFieldsForType =
        localOrder.appliedTo === "registrations"
          ? allRegistrations
          : allTickets;

      if (localOrder.items.length === apiFieldsForType.length) {
        return {
          registrationsFields:
            localOrder.appliedTo === "registrations"
              ? localOrder.items
              : allRegistrations,
          ticketsFields:
            localOrder.appliedTo === "ticket" ? localOrder.items : allTickets,
        };
      }
    }

    return { registrationsFields: allRegistrations, ticketsFields: allTickets };
  }, [data, localOrder]);

  if (isLoading && !localOrder) return <Loader />;

  const handleDragEnd = (result, appliedTo) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const currentList =
      appliedTo === "registrations" ? registrationsFields : ticketsFields;
    const newItems = [...currentList];
    const [movedItem] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, movedItem);

    setLocalOrder({ appliedTo, items: newItems });

    const updates = newItems.map((f, i) => ({ id: f.id, order: i + 1 }));

    orderMutation.mutate(
      { eventId: event?.id, appliedTo, updates },
      {
        onError: () => {
          setLocalOrder(null);
        },
      }
    );
  };

  if (appliedTo) {
    return (
      <Card shadow="sm" padding="lg" radius="md" mt="sm" withBorder>
        <DraggableSection
          fields={
            appliedTo === "registrations" ? registrationsFields : ticketsFields
          }
          appliedTo={appliedTo}
          onDragEnd={handleDragEnd}
          event={event}
          registrationsEnabled={registrationsEnabled}
        />
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" mt="sm" withBorder>
      <Stack gap="lg">
        <DraggableSection
          title="Registrations"
          fields={registrationsFields}
          appliedTo="registrations"
          onDragEnd={handleDragEnd}
          event={event}
          registrationsEnabled={registrationsEnabled}
        />
        <DraggableSection
          title="Tickets"
          fields={ticketsFields}
          appliedTo="ticket"
          onDragEnd={handleDragEnd}
          event={event}
          registrationsEnabled={registrationsEnabled}
        />
      </Stack>
    </Card>
  );
}

function DraggableSection({
  title,
  fields,
  appliedTo,
  onDragEnd,
  event,
  registrationsEnabled,
}) {
  return (
    <Stack gap="sm" mt={title ? "lg" : 0}>
      {title && (
        <>
          <Title order={4}>{title}</Title>
          <Divider />
        </>
      )}
      {fields.length === 0 ? (
        <Text c="dimmed">
          {title ? `No ${title.toLowerCase()} fields` : "No custom fields"}
        </Text>
      ) : (
        <DragDropContext onDragEnd={(result) => onDragEnd(result, appliedTo)}>
          <Droppable droppableId={`droppable-${appliedTo}`}>
            {(provided) => (
              <Stack
                gap="md"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {fields.map((field, index) => (
                  <Draggable
                    key={field.id}
                    draggableId={String(field.id)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.5 : 1,
                        }}
                      >
                        <CustomFieldCard
                          fieldData={field}
                          event={event}
                          isDraggable={true}
                          registrationsEnabled={registrationsEnabled}
                          showAppliedTo={Boolean(title)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Stack>
  );
}

export default CustomFieldsList;
